import { useEffect, useMemo, useRef, useState } from 'react';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import FormattedDateInput from '../../components/ui/FormattedDateInput';
import WizardSelect from '../../components/ui/WizardSelect';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../../components/criminal/CriminalReviewUi';
import type { LookupItem } from '../../mocks/criminalMasterData';
import {
  getDistricts,
  getPublicPetitionRequestViewData,
  savePublicPetitionRequest,
  type PublicPetitionRequestViewData,
} from '../../services/publicPetitionCaseRequests';
import type { PublicPetitionSaveStep4 } from '../../types/publicPetitionApi';
import type { PublicPetitionWizardDraft } from '../../types/publicPetitionCaseRequest';
import type { CriminalSaveStep4 } from '../../types/criminalApi';
import type { CriminalWizardDraft } from '../../types/criminalCaseRequest';
import {
  CONTACT_NUMBER_LENGTH,
  contactNumberError,
  sliceMax,
} from '../../utils/caseFieldValidation';
import { formatDisplayDateTime } from '../../utils/dateFormat';
import { setFlashToast } from '../../utils/flashToast';
import { focusFirstInvalid } from '../../utils/requiredValidation';
import { deliveryMethodDisplay, submitterFromApiStep } from '../view-request/viewRequestHelpers';
import type { PublicPetitionWizardStep } from '../wizard/publicPetition/publicPetitionConstants';
import {
  isHighUrgencyLevel,
  isPublicPetitionUrgencyJustificationRequired,
  MAX_NUMBER_OF_RESPONDENTS_DIGITS,
  minDeadlineDateForPublicPetition,
  onlyDigitsMax,
} from '../wizard/publicPetition/publicPetitionHelpers';
import { validatePublicPetitionStep } from '../wizard/publicPetition/publicPetitionStepValidation';
import { useCaseCategorization } from '../wizard/publicPetition/useCaseCategorization';
import { usePublicPetitionLookups } from '../wizard/publicPetition/usePublicPetitionLookups';
import { FieldError, inpClass } from './criminalFieldUi';
import {
  confirmCaseUpdateCancel,
  formatSubmittedAtLabel,
  isCaseRequestUuid,
  mergeSavedStepIntoInitialBaseline,
  toSelectOpts,
} from './taskCaseUpdateHelpers';
import TaskCaseUpdateSectionActions from './TaskCaseUpdateSectionActions';

type Props = {
  caseRequestId: string;
  recentSubmittedAt: string;
  readOnly?: boolean;
  viewData?: PublicPetitionRequestViewData | null;
  viewDataLoading?: boolean;
  onCancelConfirmed?: () => void;
};

const MAX_DESC = 2000;
const MAX_REF = 255;
const MAX_TEXT = 2000;
const MAX_CONTACT = CONTACT_NUMBER_LENGTH;

type EditableStep = 1 | 2 | 3;

function cloneDraft(d: PublicPetitionWizardDraft): PublicPetitionWizardDraft {
  return {
    ...d,
    applicableAreas: [...d.applicableAreas],
    applicableAreaIds: [...d.applicableAreaIds],
    deliveryMethodIds: [...d.deliveryMethodIds],
    deliveryMethods: [...d.deliveryMethods],
    documents: d.documents.map((doc) => ({ ...doc })),
  };
}

function draftHasUnsavedChanges(
  current: PublicPetitionWizardDraft,
  saved: PublicPetitionWizardDraft,
): boolean {
  return JSON.stringify(current) !== JSON.stringify(saved);
}

function pickStep1Snapshot(d: PublicPetitionWizardDraft) {
  return {
    description: d.description,
    caseMatter: d.caseMatter,
    provinceId: d.provinceId,
    districtId: d.districtId,
    yourReferenceNumber: d.yourReferenceNumber,
    otherExternalReferences: d.otherExternalReferences,
    caseNumber: d.caseNumber,
    hasConnectedMatters: d.hasConnectedMatters,
    connectedCaseNumber: d.connectedCaseNumber,
    connectedAgdFileRefAvailable: d.connectedAgdFileRefAvailable,
    connectedAgdFileRef: d.connectedAgdFileRef,
  };
}

function pickStep2Snapshot(d: PublicPetitionWizardDraft) {
  return {
    petitionerName: d.petitionerName,
    nicOrIdentificationNumber: d.nicOrIdentificationNumber,
    petitionerAddress: d.petitionerAddress,
    petitionerContactNumber: d.petitionerContactNumber,
    petitionerEmail: d.petitionerEmail,
    representedByAttorney: d.representedByAttorney,
    attorneyName: d.attorneyName,
    attorneyContactNumber: d.attorneyContactNumber,
    respondentNames: d.respondentNames,
    respondentTypeId: d.respondentTypeId,
    respondentAddress: d.respondentAddress,
    numberOfRespondents: d.numberOfRespondents,
  };
}

function pickStep3Snapshot(d: PublicPetitionWizardDraft) {
  return {
    urgencyLevelId: d.urgencyLevelId,
    deadline: d.deadline,
    urgencyJustification: d.urgencyJustification,
  };
}

function stepHasData(step: EditableStep, d: PublicPetitionWizardDraft): boolean {
  if (step === 1) {
    return !!(
      d.description.trim() ||
      d.yourReferenceNumber.trim() ||
      d.provinceId.trim() ||
      d.caseNumber.trim() ||
      d.caseMatter
    );
  }
  if (step === 2) {
    return !!(d.petitionerName.trim() || d.petitionerAddress.trim() || d.respondentNames.trim());
  }
  return !!(d.urgencyLevelId.trim() || d.deadline.trim() || d.urgencyJustification.trim());
}

function canSaveStep(
  step: EditableStep,
  draft: PublicPetitionWizardDraft | null,
  initial: PublicPetitionWizardDraft | null,
): boolean {
  if (!draft || !initial) return false;
  const pick = step === 1 ? pickStep1Snapshot : step === 2 ? pickStep2Snapshot : pickStep3Snapshot;
  const changed = JSON.stringify(pick(draft)) !== JSON.stringify(pick(initial));
  return (stepHasData(step, draft) || stepHasData(step, initial)) && changed;
}

function mergeLookupItems(
  items: { id: string; name: string }[],
  selectedId: string,
  selectedName: string,
): { id: string; name: string }[] {
  if (!selectedId || items.some((x) => x.id === selectedId)) return items;
  return [...items, { id: selectedId, name: selectedName || selectedId }];
}

export default function TaskCasePublicPetitionUpdateTab({
  caseRequestId,
  recentSubmittedAt,
  readOnly = false,
  viewData,
  viewDataLoading = false,
  onCancelConfirmed,
}: Props) {
  const [draft, setDraft] = useState<PublicPetitionWizardDraft | null>(null);
  const [initial, setInitial] = useState<PublicPetitionWizardDraft | null>(null);
  const [step4, setStep4] = useState<PublicPetitionSaveStep4 | undefined>();
  const [submittedLabel, setSubmittedLabel] = useState(recentSubmittedAt);
  const [loadedForKey, setLoadedForKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState('');
  const [savingStep, setSavingStep] = useState<PublicPetitionWizardStep | null>(null);
  const saveInFlightRef = useRef(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [districts, setDistricts] = useState<LookupItem[]>([]);
  const isSaving = savingStep !== null;

  const lookups = usePublicPetitionLookups('review');
  const cat = useCaseCategorization(draft?.fileCategoryId ?? '', draft?.subFileCategoryId ?? '');
  const minDeadlineDate = useMemo(() => minDeadlineDateForPublicPetition(), []);
  const useSharedViewData = viewData !== undefined || viewDataLoading;
  const loadKey = `${caseRequestId}|${recentSubmittedAt}|${viewData?.submittedAt ?? ''}|${viewData?.draft.draftRequestId ?? ''}`;
  const loading = useSharedViewData
    ? viewDataLoading || loadedForKey !== loadKey
    : loadedForKey !== loadKey;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!isCaseRequestUuid(caseRequestId)) {
        if (cancelled) return;
        setDraft(null);
        setInitial(null);
        setLoadError('Case request id is missing or invalid.');
        setLoadedForKey(loadKey);
        return;
      }

      if (useSharedViewData) {
        if (viewDataLoading) return;
        if (!viewData?.draft) {
          if (cancelled) return;
          setDraft(null);
          setInitial(null);
          setStep4(undefined);
          setLoadError('Could not load case update data.');
          setLoadedForKey(loadKey);
          return;
        }
        if (cancelled) return;
        const loaded = cloneDraft(viewData.draft);
        setDraft(loaded);
        setInitial(cloneDraft(loaded));
        setStep4(viewData.step4);
        setSubmittedLabel(formatSubmittedAtLabel(viewData.submittedAt, recentSubmittedAt));
        setLoadError('');
        setLoadedForKey(loadKey);
        return;
      }

      const res = await getPublicPetitionRequestViewData(caseRequestId);
      if (cancelled) return;
      if (!res.success || !res.data?.draft) {
        setDraft(null);
        setInitial(null);
        setStep4(undefined);
        setLoadError((!res.success ? res.message : undefined) ?? 'Could not load case update data.');
        setLoadedForKey(loadKey);
        return;
      }
      const loaded = cloneDraft(res.data.draft);
      setDraft(loaded);
      setInitial(cloneDraft(loaded));
      setStep4(res.data.step4);
      setSubmittedLabel(formatSubmittedAtLabel(res.data.submittedAt, recentSubmittedAt));
      setLoadError('');
      setLoadedForKey(loadKey);
    })();
    return () => {
      cancelled = true;
    };
  }, [caseRequestId, loadKey, recentSubmittedAt, useSharedViewData, viewData, viewDataLoading]);

  useEffect(() => {
    const provinceId = draft?.provinceId?.trim() ?? '';
    if (!provinceId) {
      setDistricts([]);
      return;
    }
    let cancelled = false;
    void getDistricts(provinceId).then((r) => {
      if (cancelled) return;
      setDistricts(r.success ? r.data : []);
    });
    return () => {
      cancelled = true;
    };
  }, [draft?.provinceId]);

  const canSaveStep1 = useMemo(() => canSaveStep(1, draft, initial), [draft, initial]);
  const canSaveStep2 = useMemo(() => canSaveStep(2, draft, initial), [draft, initial]);
  const canSaveStep3 = useMemo(() => canSaveStep(3, draft, initial), [draft, initial]);
  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));

  const saveSection = async (step: EditableStep) => {
    if (!draft || readOnly || saveInFlightRef.current) return;
    const selectedFileCategory = cat.fileCategories.find((c) => c.id === draft.fileCategoryId);
    const result = validatePublicPetitionStep(step, draft, {
      subCategories: cat.subCategories,
      loadingSubCategories: cat.loadingSubCategories,
      fileCategoryCode: selectedFileCategory?.code,
      urgencyLevels: lookups.urgencyLevels,
    });
    setErrors(result.errors);
    if (!result.valid) {
      focusFirstInvalid(result.specs, result.errors);
      return;
    }

    saveInFlightRef.current = true;
    setSavingStep(step);
    try {
      const r = await savePublicPetitionRequest(draft, {
        isDraft: false,
        confirmedCorrect: true,
        currentStep: step,
        isUpdateAlreadySubmitted: true,
      });
      if (!r.success) {
        setFlashToast({ message: r.message ?? 'Failed to save case update.', variant: 'error' });
        return;
      }
      const next = {
        ...draft,
        draftRequestId: r.data.requestId || draft.draftRequestId,
        referenceNo: r.data.fileReferenceNumber ?? draft.referenceNo,
      };
      setDraft(next);
      setInitial((prev) => {
        if (!prev) return cloneDraft(next);
        const pick =
          step === 1 ? pickStep1Snapshot : step === 2 ? pickStep2Snapshot : pickStep3Snapshot;
        return mergeSavedStepIntoInitialBaseline(prev, next, pick, cloneDraft);
      });
      setErrors({});
      setFlashToast({ message: 'Case update saved successfully.', variant: 'success' });
    } finally {
      saveInFlightRef.current = false;
      setSavingStep(null);
    }
  };

  const resetSection = () => {
    if (initial) {
      setDraft(cloneDraft(initial));
      setErrors({});
    }
  };

  const beginCancel = () => {
    if (isSaving || !draft || !initial) return;
    if (draftHasUnsavedChanges(draft, initial)) {
      setCancelConfirmOpen(true);
      return;
    }
    resetSection();
  };

  if (loading) return <p className="tcd-update-ts">Loading case update…</p>;
  if (!draft) {
    return (
      <div className="tcd-case-update-error" role="alert">
        <p className="tcd-update-ts" style={{ color: 'var(--red)', margin: 0 }}>
          {loadError || 'Could not load case update data.'}
        </p>
      </div>
    );
  }

  const submitter = submitterFromApiStep(step4);
  const deliveryLabel = deliveryMethodDisplay(
    draft as unknown as CriminalWizardDraft,
    lookups.deliveryMethods,
    step4 as CriminalSaveStep4 | undefined,
  );
  const urgencyJustificationRequired = isPublicPetitionUrgencyJustificationRequired(
    draft,
    lookups.urgencyLevels,
  );
  const provinceOptions = toSelectOpts(
    mergeLookupItems(lookups.provinces, draft.provinceId, draft.provinceName),
  );
  const districtOptions = toSelectOpts(
    mergeLookupItems(districts, draft.districtId, draft.districtName),
  );
  const respondentTypeOptions = toSelectOpts(
    mergeLookupItems(lookups.respondentTypes, draft.respondentTypeId, draft.respondentTypeName),
  );

  return (
    <>
      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title="Cancel Changes"
        message="Are you sure you want to cancel? Any unsaved changes will be lost."
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          confirmCaseUpdateCancel(resetSection, () => setCancelConfirmOpen(false), onCancelConfirmed);
        }}
      />

      <div className="tcd-case-update">
        <p className="tcd-update-ts">
          Request Submitted at:{' '}
          <strong>{submittedLabel || formatDisplayDateTime(recentSubmittedAt) || '—'}</strong>
        </p>

        <ReviewSection step="Step 01" title="Case Overview">
          <ReviewSubBlock title="Case Categorization">
            <ReviewGrid cols={4}>
              <ReviewField label="File Category" value={draft.fileCategory} />
              <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
            </ReviewGrid>
            <ReviewChips items={draft.applicableAreas} />
          </ReviewSubBlock>

          <ReviewSubBlock title="Case Intake Details">
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>
                Description <span className="req">*</span>
              </label>
              <textarea
                id="pp-description"
                className={inpClass(Boolean(errors.description))}
                rows={4}
                maxLength={MAX_DESC}
                value={draft.description}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) => (d ? { ...d, description: sliceMax(e.target.value, MAX_DESC) } : d));
                  clearErr('description');
                }}
              />
              <FieldError message={errors.description} />
            </div>

            <div className="ccr-fg" id="pp-caseMatter" style={{ marginBottom: 14 }}>
              <label>
                Case Matter <span className="req">*</span>
              </label>
              <div className="ccr-radio-row">
                {(['CRIMINAL', 'CIVIL'] as const).map((v) => (
                  <label key={v} className="ccr-radio">
                    <input
                      type="radio"
                      name="pp-caseMatter"
                      checked={draft.caseMatter === v}
                      disabled={readOnly}
                      onChange={() => {
                        setDraft((d) => (d ? { ...d, caseMatter: v } : d));
                        clearErr('caseMatter');
                      }}
                    />
                    {v === 'CRIMINAL' ? 'Criminal Matter' : 'Civil Matter'}
                  </label>
                ))}
              </div>
              <FieldError message={errors.caseMatter} />
            </div>

            <div className="ccr-fr c2">
              <div className="ccr-fg">
                <label>
                  Province <span className="req">*</span>
                </label>
                <WizardSelect
                  id="pp-province"
                  hasError={Boolean(errors.provinceId)}
                  value={draft.provinceId}
                  disabled={readOnly}
                  placeholder="Select Province"
                  options={provinceOptions}
                  onChange={(id) => {
                    const name = lookups.provinces.find((p) => p.id === id)?.name ?? '';
                    setDraft((d) =>
                      d
                        ? {
                            ...d,
                            provinceId: id,
                            provinceName: name,
                            districtId: '',
                            districtName: '',
                          }
                        : d,
                    );
                    clearErr('provinceId');
                  }}
                />
                <FieldError message={errors.provinceId} />
              </div>
              <div className="ccr-fg">
                <label>District</label>
                <WizardSelect
                  id="pp-district"
                  value={draft.districtId}
                  disabled={readOnly || !draft.provinceId}
                  placeholder={draft.provinceId ? 'Select District' : 'Select province first'}
                  options={districtOptions}
                  onChange={(id) => {
                    const name = districts.find((x) => x.id === id)?.name ?? '';
                    setDraft((d) => (d ? { ...d, districtId: id, districtName: name } : d));
                  }}
                />
              </div>
              <div className="ccr-fg">
                <label>
                  Your Reference Number (External) <span className="req">*</span>
                </label>
                <input
                  id="pp-yourRef"
                  className={inpClass(Boolean(errors.yourReferenceNumber))}
                  value={draft.yourReferenceNumber}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, yourReferenceNumber: sliceMax(e.target.value, MAX_REF) } : d,
                    );
                    clearErr('yourReferenceNumber');
                  }}
                />
                <FieldError message={errors.yourReferenceNumber} />
              </div>
              <div className="ccr-fg">
                <label>Other External Reference Number(s)</label>
                <input
                  className={inpClass(false)}
                  value={draft.otherExternalReferences}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) =>
                    setDraft((d) =>
                      d ? { ...d, otherExternalReferences: sliceMax(e.target.value, MAX_REF) } : d,
                    )
                  }
                />
              </div>
              <div className="ccr-fg">
                <label>Case Number</label>
                <input
                  className={inpClass(false)}
                  value={draft.caseNumber}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) =>
                    setDraft((d) => (d ? { ...d, caseNumber: sliceMax(e.target.value, MAX_REF) } : d))
                  }
                />
              </div>
              <div className="ccr-fg" id="pp-connected">
                <label>
                  Are there any connected matters (Cases)? <span className="req">*</span>
                </label>
                <div className="ccr-radio-row">
                  {(['yes', 'no'] as const).map((v) => (
                    <label key={v} className="ccr-radio">
                      <input
                        type="radio"
                        name="pp-connected"
                        checked={draft.hasConnectedMatters === v}
                        disabled={readOnly}
                        onChange={() => {
                          setDraft((d) =>
                            d
                              ? {
                                  ...d,
                                  hasConnectedMatters: v,
                                  ...(v === 'no'
                                    ? {
                                        connectedCaseNumber: '',
                                        connectedAgdFileRefAvailable: false,
                                        connectedAgdFileRef: '',
                                      }
                                    : {}),
                                }
                              : d,
                          );
                          clearErr('hasConnectedMatters');
                        }}
                      />
                      {v === 'yes' ? 'Yes' : 'No'}
                    </label>
                  ))}
                </div>
                <FieldError message={errors.hasConnectedMatters} />
              </div>
              {draft.hasConnectedMatters === 'yes' ? (
                <>
                  <div className="ccr-fg">
                    <label>
                      Connected Case Number <span className="req">*</span>
                    </label>
                    <input
                      id="pp-connectedCase"
                      className={inpClass(Boolean(errors.connectedCaseNumber))}
                      value={draft.connectedCaseNumber}
                      readOnly={readOnly}
                      disabled={readOnly}
                      maxLength={MAX_REF}
                      onChange={(e) => {
                        setDraft((d) =>
                          d ? { ...d, connectedCaseNumber: sliceMax(e.target.value, MAX_REF) } : d,
                        );
                        clearErr('connectedCaseNumber');
                      }}
                    />
                    <FieldError message={errors.connectedCaseNumber} />
                  </div>
                  <div className="ccr-fg">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <label style={{ marginBottom: 0 }}>
                        Connected AGD File Reference Number{' '}
                        {draft.connectedAgdFileRefAvailable ? <span className="req">*</span> : null}
                      </label>
                      <input
                        type="checkbox"
                        style={{ width: 15, height: 15, accentColor: '#000', cursor: 'pointer' }}
                        checked={draft.connectedAgdFileRefAvailable}
                        disabled={readOnly}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setDraft((d) =>
                            d
                              ? {
                                  ...d,
                                  connectedAgdFileRefAvailable: checked,
                                  connectedAgdFileRef: checked ? d.connectedAgdFileRef : '',
                                }
                              : d,
                          );
                          clearErr('connectedAgdFileRef');
                        }}
                      />
                      <span>Available</span>
                    </div>
                    <input
                      id="pp-connectedAgd"
                      className={inpClass(Boolean(errors.connectedAgdFileRef))}
                      value={draft.connectedAgdFileRef}
                      readOnly={readOnly || !draft.connectedAgdFileRefAvailable}
                      disabled={readOnly || !draft.connectedAgdFileRefAvailable}
                      maxLength={MAX_REF}
                      onChange={(e) => {
                        setDraft((d) =>
                          d ? { ...d, connectedAgdFileRef: sliceMax(e.target.value, MAX_REF) } : d,
                        );
                        clearErr('connectedAgdFileRef');
                      }}
                    />
                    <FieldError message={errors.connectedAgdFileRef} />
                  </div>
                </>
              ) : null}
            </div>
          </ReviewSubBlock>

          <TaskCaseUpdateSectionActions
            canSave={canSaveStep1}
            saving={savingStep === 1}
            readOnly={readOnly}
            onSave={() => void saveSection(1)}
            onCancel={beginCancel}
          />
        </ReviewSection>

        <ReviewSection step="Step 02" title="Parties Involved">
          <ReviewSubBlock title="Petitioner Details">
            <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
              <div className="ccr-fg">
                <label>
                  Name <span className="req">*</span>
                </label>
                <input
                  id="pp-petitionerName"
                  className={inpClass(Boolean(errors.petitionerName))}
                  value={draft.petitionerName}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, petitionerName: sliceMax(e.target.value, MAX_REF) } : d,
                    );
                    clearErr('petitionerName');
                  }}
                />
                <FieldError message={errors.petitionerName} />
              </div>
              <div className="ccr-fg">
                <label>NIC / Identification Number</label>
                <input
                  className={inpClass(false)}
                  value={draft.nicOrIdentificationNumber}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) =>
                    setDraft((d) =>
                      d
                        ? { ...d, nicOrIdentificationNumber: sliceMax(e.target.value, MAX_REF) }
                        : d,
                    )
                  }
                />
              </div>
            </div>

            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>
                Address <span className="req">*</span>
              </label>
              <textarea
                id="pp-petitionerAddress"
                className={inpClass(Boolean(errors.petitionerAddress))}
                rows={3}
                maxLength={MAX_DESC}
                value={draft.petitionerAddress}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, petitionerAddress: sliceMax(e.target.value, MAX_DESC) } : d,
                  );
                  clearErr('petitionerAddress');
                }}
              />
              <FieldError message={errors.petitionerAddress} />
            </div>

            <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
              <div className="ccr-fg">
                <label>Contact Number</label>
                <input
                  id="pp-petitionerContact"
                  className={inpClass(Boolean(errors.petitionerContactNumber))}
                  inputMode="numeric"
                  value={draft.petitionerContactNumber}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_CONTACT}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, MAX_CONTACT);
                    setDraft((d) => (d ? { ...d, petitionerContactNumber: digits } : d));
                    const err = contactNumberError(digits);
                    setErrors((prev) => ({ ...prev, petitionerContactNumber: err ?? '' }));
                  }}
                />
                <FieldError message={errors.petitionerContactNumber} />
              </div>
              <div className="ccr-fg">
                <label>Email</label>
                <input
                  className={inpClass(false)}
                  type="email"
                  value={draft.petitionerEmail}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) =>
                    setDraft((d) =>
                      d ? { ...d, petitionerEmail: sliceMax(e.target.value, MAX_REF) } : d,
                    )
                  }
                />
              </div>
            </div>

            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>Represented by Attorney</label>
              <div className="ccr-radio-row">
                {(['yes', 'no'] as const).map((v) => (
                  <label key={v} className="ccr-radio">
                    <input
                      type="radio"
                      name="pp-representedByAttorney"
                      checked={v === 'yes' ? draft.representedByAttorney : !draft.representedByAttorney}
                      disabled={readOnly}
                      onChange={() => {
                        const yes = v === 'yes';
                        setDraft((d) =>
                          d
                            ? {
                                ...d,
                                representedByAttorney: yes,
                                ...(!yes ? { attorneyName: '', attorneyContactNumber: '' } : {}),
                              }
                            : d,
                        );
                      }}
                    />
                    {v === 'yes' ? 'Yes' : 'No'}
                  </label>
                ))}
              </div>
            </div>

            {draft.representedByAttorney ? (
              <div className="ccr-fr c2">
                <div className="ccr-fg">
                  <label>Attorney Name</label>
                  <input
                    className={inpClass(false)}
                    value={draft.attorneyName}
                    readOnly={readOnly}
                    disabled={readOnly}
                    maxLength={MAX_REF}
                    onChange={(e) =>
                      setDraft((d) =>
                        d ? { ...d, attorneyName: sliceMax(e.target.value, MAX_REF) } : d,
                      )
                    }
                  />
                </div>
                <div className="ccr-fg">
                  <label>Attorney&apos;s Contact Number</label>
                  <input
                    id="pp-attorneyContact"
                    className={inpClass(Boolean(errors.attorneyContactNumber))}
                    inputMode="numeric"
                    value={draft.attorneyContactNumber}
                    readOnly={readOnly}
                    disabled={readOnly}
                    maxLength={MAX_CONTACT}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, MAX_CONTACT);
                      setDraft((d) => (d ? { ...d, attorneyContactNumber: digits } : d));
                      const err = contactNumberError(digits);
                      setErrors((prev) => ({ ...prev, attorneyContactNumber: err ?? '' }));
                    }}
                  />
                  <FieldError message={errors.attorneyContactNumber} />
                </div>
              </div>
            ) : null}
          </ReviewSubBlock>

          <ReviewSubBlock title="Respondent Details">
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>
                Respondent Name(s) <span className="req">*</span>
              </label>
              <textarea
                id="pp-respondentNames"
                className={inpClass(Boolean(errors.respondentNames))}
                rows={3}
                maxLength={MAX_DESC}
                value={draft.respondentNames}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, respondentNames: sliceMax(e.target.value, MAX_DESC) } : d,
                  );
                  clearErr('respondentNames');
                }}
              />
              <FieldError message={errors.respondentNames} />
            </div>

            <div className="ccr-fr c2">
              <div className="ccr-fg">
                <label>Respondent Type</label>
                <WizardSelect
                  value={draft.respondentTypeId}
                  disabled={readOnly}
                  placeholder="Select Respondent Type"
                  options={respondentTypeOptions}
                  onChange={(id) => {
                    const name =
                      respondentTypeOptions.find((o) => o.id === id)?.label ??
                      lookups.respondentTypes.find((x) => x.id === id)?.name ??
                      '';
                    setDraft((d) =>
                      d ? { ...d, respondentTypeId: id, respondentTypeName: name } : d,
                    );
                  }}
                />
              </div>
              <div className="ccr-fg">
                <label>Number of Respondents</label>
                <input
                  id="pp-numberOfRespondents"
                  className={inpClass(Boolean(errors.numberOfRespondents))}
                  inputMode="numeric"
                  value={draft.numberOfRespondents}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) => {
                    const digits = onlyDigitsMax(e.target.value, MAX_NUMBER_OF_RESPONDENTS_DIGITS);
                    setDraft((d) => (d ? { ...d, numberOfRespondents: digits } : d));
                    clearErr('numberOfRespondents');
                  }}
                />
                <FieldError message={errors.numberOfRespondents} />
              </div>
              <div className="ccr-fg" style={{ gridColumn: '1 / -1' }}>
                <label>Respondent Address</label>
                <textarea
                  className={inpClass(false)}
                  rows={3}
                  maxLength={MAX_DESC}
                  value={draft.respondentAddress}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) =>
                    setDraft((d) =>
                      d ? { ...d, respondentAddress: sliceMax(e.target.value, MAX_DESC) } : d,
                    )
                  }
                />
              </div>
            </div>
          </ReviewSubBlock>

          <TaskCaseUpdateSectionActions
            canSave={canSaveStep2}
            saving={savingStep === 2}
            readOnly={readOnly}
            onSave={() => void saveSection(2)}
            onCancel={beginCancel}
          />
        </ReviewSection>

        <ReviewSection step="Step 03" title="Timeline">
          <ReviewSubBlock title="Timeline">
            <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
              <div className="ccr-fg">
                <label>
                  Urgency Level <span className="req">*</span>
                </label>
                <WizardSelect
                  id="pp-urgency"
                  hasError={Boolean(errors.urgencyLevelId)}
                  value={draft.urgencyLevelId}
                  disabled={readOnly}
                  placeholder="Select Urgency Level"
                  options={toSelectOpts(lookups.urgencyLevels)}
                  onChange={(id) => {
                    const name = lookups.urgencyLevels.find((u) => u.id === id)?.name ?? '';
                    setDraft((d) => (d ? { ...d, urgencyLevelId: id, urgencyLevelName: name } : d));
                    if (!isHighUrgencyLevel(name)) clearErr('urgencyJustification');
                    clearErr('urgencyLevelId');
                  }}
                />
                <FieldError message={errors.urgencyLevelId} />
              </div>
              <div className="ccr-fg">
                <label>Deadline (if any)</label>
                <FormattedDateInput
                  id="pp-deadline"
                  className={inpClass(Boolean(errors.deadline))}
                  value={draft.deadline}
                  minDate={minDeadlineDate}
                  enableYmdViews
                  disabled={readOnly}
                  onChange={(next) => {
                    setDraft((d) => (d ? { ...d, deadline: next } : d));
                    clearErr('deadline');
                  }}
                />
                <FieldError message={errors.deadline} />
              </div>
            </div>
            <div className="ccr-fg">
              <label>
                Urgency Justification{' '}
                {urgencyJustificationRequired ? <span className="req">*</span> : null}
              </label>
              <textarea
                id="pp-urgencyJustification"
                className={inpClass(Boolean(errors.urgencyJustification))}
                rows={3}
                maxLength={MAX_TEXT}
                value={draft.urgencyJustification}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, urgencyJustification: sliceMax(e.target.value, MAX_TEXT) } : d,
                  );
                  clearErr('urgencyJustification');
                }}
              />
              <FieldError message={errors.urgencyJustification} />
            </div>
          </ReviewSubBlock>

          <TaskCaseUpdateSectionActions
            canSave={canSaveStep3}
            saving={savingStep === 3}
            readOnly={readOnly}
            onSave={() => void saveSection(3)}
            onCancel={beginCancel}
          />
        </ReviewSection>

        <ReviewSection step="Step 04" title="Submission & Physical File Delivery">
          <ReviewSubBlock title="Request Submitter's Details">
            <ReviewGrid cols={4}>
              <ReviewField label="Submitted Division/ Unit" value={submitter.division} />
              <ReviewField label="Designation" value={submitter.designation} />
              <ReviewField label="Submitted by" value={submitter.name} />
              <ReviewField label="Email Address" value={submitter.email} />
              <ReviewField label="Contact Number" value={submitter.contact} />
            </ReviewGrid>
          </ReviewSubBlock>
          <ReviewSubBlock title="Physical File Delivery Method">
            <ReviewField label="Delivery Method" value={deliveryLabel} fullWidth />
            {draft.deliveryOther.trim() ? (
              <ReviewField label="Other Delivery Method" value={draft.deliveryOther} />
            ) : null}
          </ReviewSubBlock>
        </ReviewSection>
      </div>
    </>
  );
}
